import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	Building2,
	ChevronDown,
	GitMerge,
	Mail,
	Plus,
	ShieldX,
} from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ActivityTimeline } from "@/components/crm/activity-timeline";
import {
	CompanyCombobox,
	type CompanySelection,
	toCompanyChoice,
} from "@/components/crm/company-combobox";
import { ConsentPanel } from "@/components/crm/consent-panel";
import { ContactSourceBadge } from "@/components/crm/contact-source-badge";
import { CrmLoadError } from "@/components/crm/crm-page";
import { DoNotContact } from "@/components/crm/do-not-contact";
import { EmailComposeDialog } from "@/components/crm/email-compose-dialog";
import { EraseContactDialog } from "@/components/crm/erase-contact-dialog";
import { InlineField, InlineRow } from "@/components/crm/inline-field";
import { MergeDialog } from "@/components/crm/merge-dialog";
import { OpportunityFormDialog } from "@/components/crm/opportunity-form";
import { OwnerSelect } from "@/components/crm/owner-select";
import { PlatformUserBadge } from "@/components/crm/platform-user-badge";
import { StageBadge } from "@/components/crm/stage-badge";
import { ContactStatusBadge } from "@/components/crm/status-badges";
import { TasksPanel } from "@/components/crm/task-list";
import { UpcomingMeetings } from "@/components/crm/upcoming-meetings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useCrmPermissions } from "@/hooks/use-crm-access";
import { useRegional } from "@/hooks/use-regional";
import { formLabel } from "@/lib/crm/activity-title";
import { CRM_CONTACT_LANGUAGES } from "@/lib/crm/constants";
import { contactName, decodeCrmError } from "@/lib/crm/format";
import { CRM_STALE_TIME, invalidateCrmData } from "@/lib/crm/queries";
import { formatDate, formatDateTime } from "@/lib/crm/regional";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/crm/contacts/$contactId")({
	component: ContactPage,
});

const NOT_SET = "__none";

/** Header buttons on a card background, so they stand out from the page. */
const HEADER_OUTLINE = "bg-card dark:bg-card";

function ContactPage(): ReactElement {
	const { contactId } = Route.useParams();
	const { t } = useTranslation();
	const regional = useRegional();
	const queryClient = useQueryClient();
	const { canManage, canAdmin } = useCrmPermissions();
	const [changingCompany, setChangingCompany] = useState(false);
	const [companyDraft, setCompanyDraft] = useState<CompanySelection>(null);
	const [dealOpen, setDealOpen] = useState(false);
	const [mergeOpen, setMergeOpen] = useState(false);
	const [eraseMode, setEraseMode] = useState<"request" | "override" | null>(
		null,
	);
	const [attributionOpen, setAttributionOpen] = useState(false);
	const [composeOpen, setComposeOpen] = useState(false);

	const { data, isLoading, isError } = useQuery({
		...orpc.admin.crm.contacts.get.queryOptions({ input: { id: contactId } }),
		staleTime: CRM_STALE_TIME,
	});
	const { data: dealsData } = useQuery({
		...orpc.admin.crm.opportunities.list.queryOptions({
			input: { contactId, limit: 50, offset: 0 },
		}),
		enabled: canManage,
		staleTime: CRM_STALE_TIME,
	});
	const update = useMutation(orpc.admin.crm.contacts.update.mutationOptions());

	if (isLoading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-10 w-1/2" />
				<Skeleton className="h-64" />
			</div>
		);
	}
	if (isError || !data) return <CrmLoadError />;

	const { contact, organization } = data;
	const erased = contact.status === "erased";
	const editable = canManage && !erased;
	const label =
		contactName(contact) ??
		t(erased ? "admin:crm.contact.erased_name" : "admin:crm.contact.unnamed");

	type ContactPatch = Parameters<typeof update.mutateAsync>[0]["patch"];
	const save = async (patch: ContactPatch): Promise<void> => {
		try {
			await update.mutateAsync({ id: contact.id, patch });
			await invalidateCrmData(queryClient);
		} catch (error) {
			const decoded = decodeCrmError(error);
			toast.error(
				decoded.code === "CONFLICT" && decoded.existingContactId
					? t("admin:crm.contact_form.duplicate_contact")
					: decoded.code === "CONFLICT"
						? t("admin:crm.contact_form.duplicate_company")
						: (decoded.message ?? t("admin:crm.errors.generic")),
			);
			throw error;
		}
	};
	const saveQuietly = (patch: ContactPatch): void => {
		save(patch).catch(() => undefined);
	};
	const textOrNull = (value: string): string | null => value.trim() || null;

	const applyCompany = async (selection: CompanySelection): Promise<void> => {
		setCompanyDraft(selection);
		// An inline new company waits for its fields; a pick saves at once.
		if (selection?.kind === "new") return;
		try {
			await save({ company: toCompanyChoice(selection) });
			setChangingCompany(false);
			setCompanyDraft(null);
		} catch {
			// Reported by save().
		}
	};

	const attribution = contact.attribution as Record<string, unknown> | null;
	const attributionEntries = attribution
		? Object.entries(attribution).filter(
				([, value]) => value !== null && value !== undefined && value !== "",
			)
		: [];
	const deals = dealsData?.opportunities ?? [];

	return (
		<div className="space-y-4">
			<Link
				to="/crm/contacts"
				className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:underline"
			>
				<ArrowLeft className="h-4 w-4" />
				{t("admin:crm.contacts.title")}
			</Link>
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="space-y-1">
					<h1 className="flex flex-wrap items-center gap-2 font-bold text-3xl">
						{label}
						<ContactStatusBadge status={contact.status} />
						{contact.platformUserId && <PlatformUserBadge />}
					</h1>
					<p className="text-muted-foreground text-sm">
						{[contact.title, organization?.name].filter(Boolean).join(" · ")}
					</p>
				</div>
				{!erased && (
					<div className="flex flex-wrap gap-2">
						{editable && contact.email && !contact.doNotContactAt && (
							<Button onClick={() => setComposeOpen(true)}>
								<Mail className="h-4 w-4" />
								{t("admin:crm.compose.title")}
							</Button>
						)}
						{editable && (
							<Button
								variant="outline"
								className={HEADER_OUTLINE}
								onClick={() => setDealOpen(true)}
							>
								<Plus className="h-4 w-4" />
								{t("admin:crm.pipeline.new_deal")}
							</Button>
						)}
						{!contact.doNotContactAt && (
							<DoNotContact
								contactId={contact.id}
								since={null}
								canAdmin={canAdmin}
							/>
						)}
						{editable && canAdmin && (
							<Button
								variant="outline"
								className={HEADER_OUTLINE}
								onClick={() => setMergeOpen(true)}
							>
								<GitMerge className="h-4 w-4" />
								{t("admin:crm.merge.button")}
							</Button>
						)}
						{editable && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="outline" className={HEADER_OUTLINE}>
										<ShieldX className="h-4 w-4" />
										{t("admin:crm.erase.menu")}
										<ChevronDown className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem
										disabled={!contact.email}
										onSelect={() => setEraseMode("request")}
									>
										{t("admin:crm.erase.request_menu")}
									</DropdownMenuItem>
									{canAdmin && (
										<DropdownMenuItem
											className="text-destructive"
											onSelect={() => setEraseMode("override")}
										>
											{t("admin:crm.erase.override_menu")}
										</DropdownMenuItem>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
				)}
			</div>

			{erased && (
				<p className="rounded-md bg-muted px-4 py-3 text-muted-foreground text-sm">
					{t("admin:crm.contact.erased_notice", {
						date: formatDate(contact.erasedAt, regional),
						reason: contact.erasureReason
							? t(`admin:crm.erasure_reason.${contact.erasureReason}`)
							: "—",
					})}
				</p>
			)}
			{!erased && contact.doNotContactAt && (
				<DoNotContact
					contactId={contact.id}
					since={contact.doNotContactAt}
					canAdmin={canAdmin}
				/>
			)}
			{contact.status === "stale" && contact.staleSince && (
				<p className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:bg-amber-950">
					{t("admin:crm.contact.stale_notice", {
						date: formatDate(contact.staleSince, regional),
					})}
				</p>
			)}

			<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
				<div className="space-y-4">
					{!erased && canManage && (
						<UpcomingMeetings
							entity={{ entityType: "contact", entityId: contact.id }}
							bookFor={
								editable && contact.email && !contact.doNotContactAt
									? { contactIds: [contact.id] }
									: undefined
							}
						/>
					)}
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base">
								{t("admin:crm.contact.details")}
							</CardTitle>
						</CardHeader>
						<CardContent>
							<dl>
								<InlineField
									label={t("admin:crm.contact.first_name")}
									value={erased ? "" : (contact.firstName ?? "")}
									editable={editable}
									onSave={(value) => save({ firstName: textOrNull(value) })}
								/>
								<InlineField
									label={t("admin:crm.contact.last_name")}
									value={erased ? "" : (contact.lastName ?? "")}
									editable={editable}
									onSave={(value) => save({ lastName: textOrNull(value) })}
								/>
								<InlineField
									label={t("admin:crm.contact.email")}
									type="email"
									value={contact.email ?? ""}
									editable={editable}
									onSave={(value) => save({ email: textOrNull(value) })}
								/>
								<InlineRow label={t("admin:crm.contact.company")}>
									{changingCompany ? (
										<div className="space-y-2 py-1">
											<CompanyCombobox
												value={companyDraft}
												onChange={(selection) => void applyCompany(selection)}
												autoFocus
											/>
											<div className="flex gap-2">
												{companyDraft?.kind === "new" && (
													<Button
														size="sm"
														disabled={
															!companyDraft.company.name.trim() ||
															update.isPending
														}
														onClick={() => {
															save({ company: toCompanyChoice(companyDraft) })
																.then(() => {
																	setChangingCompany(false);
																	setCompanyDraft(null);
																})
																.catch(() => undefined);
														}}
													>
														{t("admin:crm.common.save")}
													</Button>
												)}
												{organization && (
													<Button
														size="sm"
														variant="ghost"
														onClick={() => void applyCompany(null)}
													>
														{t("admin:crm.contact.remove_company")}
													</Button>
												)}
												<Button
													size="sm"
													variant="ghost"
													onClick={() => {
														setChangingCompany(false);
														setCompanyDraft(null);
													}}
												>
													{t("admin:crm.common.cancel")}
												</Button>
											</div>
										</div>
									) : (
										<div className="flex items-center gap-2">
											{organization ? (
												<Link
													to="/crm/organizations/$organizationId"
													params={{ organizationId: organization.id }}
													className="inline-flex items-center gap-1 hover:underline"
												>
													<Building2 className="h-3.5 w-3.5" />
													{organization.name}
												</Link>
											) : (
												<span className="text-muted-foreground">—</span>
											)}
											{editable && (
												<Button
													size="sm"
													variant="ghost"
													className="h-7"
													onClick={() => setChangingCompany(true)}
												>
													{t("admin:crm.contact.change_company")}
												</Button>
											)}
										</div>
									)}
								</InlineRow>
								<InlineField
									label={t("admin:crm.contact.phone")}
									type="tel"
									value={contact.phone ?? ""}
									editable={editable}
									onSave={(value) => save({ phone: textOrNull(value) })}
								/>
								<InlineField
									label={t("admin:crm.contact.title")}
									value={contact.title ?? ""}
									editable={editable}
									onSave={(value) => save({ title: textOrNull(value) })}
								/>
								<InlineField
									label={t("admin:crm.contact.role")}
									value={contact.role ?? ""}
									editable={editable}
									onSave={(value) => save({ role: textOrNull(value) })}
								/>
								<InlineRow label={t("admin:crm.contact.language")}>
									{editable ? (
										<Select
											value={contact.preferredLanguage ?? NOT_SET}
											onValueChange={(value) =>
												saveQuietly({
													preferredLanguage: value === NOT_SET ? null : value,
												})
											}
										>
											<SelectTrigger
												className="h-8"
												aria-label={t("admin:crm.contact.language")}
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value={NOT_SET}>
													{t("admin:crm.common.not_set")}
												</SelectItem>
												{CRM_CONTACT_LANGUAGES.map((code) => (
													<SelectItem key={code} value={code}>
														{t(`admin:crm.language.${code}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									) : contact.preferredLanguage ? (
										t(`admin:crm.language.${contact.preferredLanguage}`, {
											defaultValue: contact.preferredLanguage,
										})
									) : (
										"—"
									)}
								</InlineRow>
								<InlineField
									label={t("admin:crm.company.country")}
									value={contact.country ?? ""}
									editable={editable}
									placeholder="FI"
									onSave={(value) =>
										save({ country: textOrNull(value)?.toUpperCase() ?? null })
									}
								/>
								<InlineRow label={t("admin:crm.common.owner")}>
									{editable ? (
										<OwnerSelect
											className="h-8"
											value={contact.ownerId}
											onChange={(value) =>
												saveQuietly({ ownerId: value ?? null })
											}
										/>
									) : (
										(data.owner?.name ?? "—")
									)}
								</InlineRow>
								<InlineRow label={t("admin:crm.common.source")}>
									<ContactSourceBadge source={contact.source} />
									{contact.sourceDetail && (
										<span className="ml-2 text-muted-foreground text-xs">
											{contact.source === "website_form"
												? formLabel(contact.sourceDetail, t)
												: contact.sourceDetail}
										</span>
									)}
								</InlineRow>
								<InlineRow label={t("admin:crm.contacts.last_activity")}>
									{formatDateTime(contact.lastActivityAt, regional)}
								</InlineRow>
								<InlineRow label={t("admin:crm.common.created")}>
									{formatDateTime(contact.createdAt, regional)}
								</InlineRow>
							</dl>
							{attributionEntries.length > 0 && (
								<div className="mt-3 border-t pt-2">
									<Button
										variant="ghost"
										size="sm"
										className="-ml-2"
										aria-expanded={attributionOpen}
										onClick={() => setAttributionOpen((open) => !open)}
									>
										<ChevronDown
											className={`h-4 w-4 transition-transform ${attributionOpen ? "" : "-rotate-90"}`}
										/>
										{t("admin:crm.contact.attribution")}
									</Button>
									{attributionOpen && (
										<dl className="mt-1 grid grid-cols-[8rem_1fr] gap-x-2 gap-y-1 text-xs">
											{attributionEntries.map(([key, value]) => (
												<div key={key} className="contents">
													<dt className="text-muted-foreground">{key}</dt>
													<dd className="break-all">
														{typeof value === "object"
															? JSON.stringify(value)
															: String(value)}
													</dd>
												</div>
											))}
										</dl>
									)}
								</div>
							)}
						</CardContent>
					</Card>

					{canManage && (
						<Card>
							<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle className="text-base">
									{t("admin:crm.deals.title")} ({deals.length})
								</CardTitle>
								{editable && (
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setDealOpen(true)}
									>
										<Plus className="h-4 w-4" />
										{t("admin:crm.pipeline.new_deal")}
									</Button>
								)}
							</CardHeader>
							<CardContent>
								{deals.length === 0 ? (
									<p className="text-muted-foreground text-sm">
										{t("admin:crm.deals.empty")}
									</p>
								) : (
									<ul className="divide-y">
										{deals.map((deal) => (
											<li
												key={deal.id}
												className="flex items-center gap-2 py-2 text-sm"
											>
												<Link
													to="/crm/opportunities/$opportunityId"
													params={{ opportunityId: deal.id }}
													className="flex-1 font-medium hover:underline"
												>
													{deal.title}
												</Link>
												<StageBadge stage={deal.stage} />
											</li>
										))}
									</ul>
								)}
							</CardContent>
						</Card>
					)}
					{canManage && !erased && (
						<TasksPanel
							parent={{ entityType: "contact", entityId: contact.id }}
							canManage={canManage}
						/>
					)}
					{/* Last: consent only matters for mass mailing, not sales contact. */}
					{!erased && (
						<ConsentPanel contactId={contact.id} canManage={canManage} />
					)}
				</div>
				<ActivityTimeline
					entity={{ entityType: "contact", entityId: contact.id }}
					canManage={editable}
				/>
			</div>

			{editable && (
				<OpportunityFormDialog
					open={dealOpen}
					onOpenChange={setDealOpen}
					defaultCompany={
						organization
							? {
									id: organization.id,
									name: organization.name,
									businessId: organization.businessId,
								}
							: null
					}
					defaultContactId={contact.id}
				/>
			)}
			{canAdmin && (
				<MergeDialog
					entityType="contact"
					sourceId={contact.id}
					sourceLabel={label}
					sourceContact={{
						source: contact.source,
						email: contact.email,
						organizationName: organization?.name ?? null,
						createdAt: contact.createdAt,
					}}
					open={mergeOpen}
					onOpenChange={setMergeOpen}
				/>
			)}
			{editable && contact.email && (
				<EmailComposeDialog
					open={composeOpen}
					onOpenChange={setComposeOpen}
					to={[contact.email]}
					links={[{ entityType: "contact", entityId: contact.id }]}
				/>
			)}
			{eraseMode && (
				<EraseContactDialog
					contactId={contact.id}
					contactLabel={label}
					mode={eraseMode}
					open
					onOpenChange={(open) => !open && setEraseMode(null)}
				/>
			)}
		</div>
	);
}
